// @ts-nocheck
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getUser } from "../auth/auth";
import { getAdminVerifications, reviewAdminVerification } from "../api/admin";
import { useToast } from "../components/ToastProvider";
import "../styles/AdminVerifications.css";

export default function AdminVerifications() {
  const navigate = useNavigate();
  const user = getUser();
  const { showToast } = useToast();
  
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [decision, setDecision] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchVerifications = useCallback(async () => {
    try {
      const data = await getAdminVerifications();
      setVerifications(data);
    } catch (error) {
      console.error("Error fetching verifications:", error);
      if (error?.message?.toLowerCase?.().includes("forbidden")) {
        showToast("Unauthorized access", "error");
        navigate("/home");
        return;
      }
      showToast("Failed to load verifications", "error");
    } finally {
      setLoading(false);
    }
  }, [navigate, showToast]);

  useEffect(() => {
    if (!user || user.role !== "admin") {
      showToast("Unauthorized access", "error");
      navigate("/home");
      return;
    }

    fetchVerifications();
  }, [user, navigate, fetchVerifications, showToast]);

  const getFilteredVerifications = () => {
    if (filter === "all") return verifications;
    return verifications.filter(v => v.status === filter);
  };

  const handleSelectVerification = (verification) => {
    setSelectedVerification(verification);
    setShowModal(true);
    setDecision("");
    setRejectionReason("");
  };

  const handleReview = async (e) => {
    e.preventDefault();
    
    if (!decision) {
      showToast("Please select approve or reject", "warning");
      return;
    }

    if (decision === "reject" && !rejectionReason.trim()) {
      showToast("Please provide a rejection reason", "warning");
      return;
    }

    setSubmitting(true);

    try {
      const result = await reviewAdminVerification(selectedVerification.id, {
        decision,
        rejectionReason: decision === "reject" ? rejectionReason : null
      });
      showToast(result.message, "success");
      
      await fetchVerifications();
      
      setShowModal(false);
      setSelectedVerification(null);
    } catch (error) {
      console.error("Error processing review:", error);
      showToast(error.message || "Failed to process review", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-verifications">
        <div className="loading">Loading verifications...</div>
      </div>
    );
  }

  const filteredVerifications = getFilteredVerifications();

  return (
    <div className="admin-verifications">
      <header className="admin-header">
        <button className="back-btn" onClick={() => navigate("/home")}>←</button>
        <div>
          <h1>Verification Management</h1>
          <p>Review seller verification applications</p>
        </div>
      </header>

      <div className="filter-bar">
        <button 
          className={filter === "pending" ? "active" : ""} 
          onClick={() => setFilter("pending")}
        >
          Pending ({verifications.filter(v => v.status === "pending").length})
        </button>
        <button 
          className={filter === "approved" ? "active" : ""} 
          onClick={() => setFilter("approved")}
        >
          Approved ({verifications.filter(v => v.status === "approved").length})
        </button>
        <button 
          className={filter === "rejected" ? "active" : ""} 
          onClick={() => setFilter("rejected")}
        >
          Rejected ({verifications.filter(v => v.status === "rejected").length})
        </button>
        <button 
          className={filter === "all" ? "active" : ""} 
          onClick={() => setFilter("all")}
        >
          All ({verifications.length})
        </button>
      </div>

      <div className="verifications-list">
        {filteredVerifications.length === 0 ? (
          <div className="empty-state">
            <p>No verifications found</p>
          </div>
        ) : (
          filteredVerifications.map(verification => (
            <div 
              key={verification.id} 
              className={`verification-card ${verification.status}`}
              onClick={() => handleSelectVerification(verification)}
            >
              <div className="verification-header">
                <span className="verification-id">{verification.id}</span>
                <span className={`status-badge ${verification.status}`}>
                  {verification.status.toUpperCase()}
                </span>
              </div>
              
              <div className="verification-content">
                <div className="verification-field">
                  <span className="label">User ID:</span>
                  <span className="value">{verification.userId}</span>
                </div>
                <div className="verification-field">
                  <span className="label">ID Type:</span>
                  <span className="value">{verification.idType?.replace("_", " ").toUpperCase()}</span>
                </div>
                <div className="verification-field">
                  <span className="label">Phone:</span>
                  <span className="value">{verification.phone}</span>
                </div>
                <div className="verification-field">
                  <span className="label">Location:</span>
                  <span className="value">{verification.city}, {verification.state}</span>
                </div>
                <div className="verification-field">
                  <span className="label">Submitted:</span>
                  <span className="value">
                    {new Date(verification.submittedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="verification-footer">
                Click to review details
              </div>
            </div>
          ))
        )}
      </div>

      {/* Verification Detail Modal */}
      {showModal && selectedVerification && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Verification Review</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="label">Verification ID:</span>
                  <span className="value">{selectedVerification.id}</span>
                </div>
                <div className="detail-row">
                  <span className="label">User ID:</span>
                  <span className="value">{selectedVerification.userId}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  <span className={`status-badge ${selectedVerification.status}`}>
                    {selectedVerification.status.toUpperCase()}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Submitted:</span>
                  <span className="value">
                    {new Date(selectedVerification.submittedAt).toLocaleString()}
                  </span>
                </div>
              </div>

              <h3 className="section-title">Identity Verification</h3>
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="label">ID Type:</span>
                  <span className="value">{selectedVerification.idType?.replace("_", " ").toUpperCase()}</span>
                </div>
                <div className="detail-row">
                  <span className="label">ID Number:</span>
                  <span className="value">{selectedVerification.idNumber}</span>
                </div>
              </div>

              <div className="document-images">
                <div className="doc-image-container">
                  <label>ID Front:</label>
                  {selectedVerification.idFrontImage && (
                    <img src={selectedVerification.idFrontImage} alt="ID Front" className="doc-image" />
                  )}
                </div>
                <div className="doc-image-container">
                  <label>ID Back:</label>
                  {selectedVerification.idBackImage && (
                    <img src={selectedVerification.idBackImage} alt="ID Back" className="doc-image" />
                  )}
                </div>
              </div>

              <h3 className="section-title">Contact Information</h3>
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="label">Phone:</span>
                  <span className="value">{selectedVerification.phone}</span>
                </div>
                <div className="detail-row full-width">
                  <span className="label">Address:</span>
                  <span className="value">
                    {selectedVerification.fullAddress}<br />
                    {selectedVerification.city}, {selectedVerification.state} {selectedVerification.zipCode}
                  </span>
                </div>
              </div>

              <div className="document-images">
                <div className="doc-image-container full-width">
                  <label>Proof of Address:</label>
                  {selectedVerification.proofOfAddressImage && (
                    <img src={selectedVerification.proofOfAddressImage} alt="Proof of Address" className="doc-image" />
                  )}
                </div>
              </div>

              {selectedVerification.status === "pending" && (
                <form className="review-form" onSubmit={handleReview}>
                  <h3 className="section-title">Review Decision</h3>
                  
                  <div className="decision-buttons">
                    <button
                      type="button"
                      className={`decision-btn approve ${decision === "approve" ? "selected" : ""}`}
                      onClick={() => setDecision("approve")}
                    >
                      ✓ Approve
                    </button>
                    <button
                      type="button"
                      className={`decision-btn reject ${decision === "reject" ? "selected" : ""}`}
                      onClick={() => setDecision("reject")}
                    >
                      ✕ Reject
                    </button>
                  </div>

                  {decision === "reject" && (
                    <label>
                      Rejection Reason:
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Explain why the verification was rejected..."
                        rows="4"
                        required
                      />
                    </label>
                  )}

                  <div className="action-buttons">
                    <button 
                      type="button" 
                      className="btn-cancel"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn-submit"
                      disabled={submitting || !decision}
                    >
                      {submitting ? "Processing..." : "Submit Review"}
                    </button>
                  </div>
                </form>
              )}

              {selectedVerification.status !== "pending" && (
                <div className="review-info">
                  <h3 className="section-title">Review Information</h3>
                  <div className="detail-grid">
                    <div className="detail-row">
                      <span className="label">Reviewed:</span>
                      <span className="value">
                        {new Date(selectedVerification.reviewedAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Reviewed By:</span>
                      <span className="value">{selectedVerification.reviewedBy}</span>
                    </div>
                    {selectedVerification.rejectionReason && (
                      <div className="detail-row full-width">
                        <span className="label">Rejection Reason:</span>
                        <span className="value">{selectedVerification.rejectionReason}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
